import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Badge } from "react-bootstrap";
import Dialog from "../Dialog";
import QuestList from "../QuestList";
import WheelButton from "./WheelButton";
import WheelRewardsInfo from "./WheelRewardsInfo";
import WheelWinDialog from "./WheelWinDialog";
import CircularProgress from "../CircularProgress";
import LuckyWheel from "./lucky-wheel";
import useLuckyWheel from "../../hooks/useLuckyWheel";
import useWheelRewardCatalog from "../../hooks/useWheelRewardCatalog";
import { formatWheelTime, WHEEL_SLOT_COUNT } from "../../utils/wheel-of-luck";
import useClaimableQuestCount from "../../hooks/useClaimableQuestCount";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import "./wheel.css";

export type EventsTab = "wheel" | "quests";
type Props = {
  open: boolean; onClose: () => void; historicalKeyId?: number;
  tab?: EventsTab; onTabChange?: (tab: EventsTab) => void;
};

export default function RewardDialog({ open, onClose, historicalKeyId, tab = "wheel", onTabChange }: Props) {
  const { t, currentLanguage } = useAppTranslation(["navigation", "wheel"]);
  const number = (value: number) => value.toLocaleString(currentLanguage);
  const claimableQuests = useClaimableQuestCount();
  const model = useLuckyWheel(open, historicalKeyId);
  const describe = useWheelRewardCatalog(open);
  const clegBalance = useSelector((state: any) => Number(state.userInfo?.totalCleg || 0));
  const { wheel, events, loading, phase, pending, serverNow } = model;
  const busy = phase !== "idle";
  const [showInfo, setShowInfo] = useState(false);
  useEffect(() => { setShowInfo(false); }, [open, model.selectedId]);
  const refreshedBoundary = useRef("");
  const resetAt = wheel?.nextResetAt ? Date.parse(wheel.nextResetAt) : NaN;
  const eventEndAt = wheel?.eventEndAt ? Date.parse(wheel.eventEndAt) : NaN;
  const freeKeyAt = wheel?.nextFreeKeyAt ? Date.parse(wheel.nextFreeKeyAt) : NaN;
  const ended = Boolean(wheel?.eventEnded || serverNow >= eventEndAt);
  const waiting = Boolean(wheel?.allSlotsClaimed || (wheel && wheel.slots.every(slot => slot.claimed)));

  // Reconcile once when each server-reported boundary is reached. A stale
  // server timestamp must not cause an endless request loop.
  useEffect(() => {
    if (!open || !wheel || loading || busy || pending || model.lastReward) return;
    const crossed = [resetAt, eventEndAt, freeKeyAt].filter(at => Number.isFinite(at) && serverNow >= at);
    if (!crossed.length) return;
    const key = `${wheel.playerWheelOfLuckId}:${crossed.join(":")}`;
    if (refreshedBoundary.current === key) return;
    refreshedBoundary.current = key;
    void model.reload();
  }, [open, wheel, loading, busy, pending, serverNow, resetAt, eventEndAt, freeKeyAt, model]);

  const reward = model.lastReward ? describe(model.lastReward) : null;
  const canUseKey = wheel?.keyAccepted && (!ended || wheel.isHistorical);
  const canUseCleg = wheel?.clegAccepted && !ended && !wheel.isHistorical;
  const spinPaymentMethod = canUseKey && (wheel.validKeyCount > 0 || !canUseCleg) ? 1 : 2;
  const timeLeft = (target: string | null) => formatWheelTime(target, serverNow,
    { language: currentLanguage, days: days => t("wheel:time.days", { days }) });
  const close = () => { if (!busy) onClose(); };
  // Closing clears the hash, so hold the last tab while the dialog fades out.
  const shownTab = useRef(tab);
  if (open) shownTab.current = tab;
  // A spin has to finish on the wheel it started on, and a leftover key opens
  // only its own wheel, so neither can switch to the Quests tab.
  const showQuests = shownTab.current === "quests" && !busy && !historicalKeyId;

  return (
    <>
    <Dialog size="lg" title={t("navigation:events")} open={open} onClose={close} force={busy} noFooter className="luck-dialog" dialogClassName="luck-modal">
      <div className="luck-layout" aria-busy={loading || busy}>
        <nav className="luck-events" aria-label={t("navigation:events")}>
          {!historicalKeyId && (
            <button type="button" className={`luck-event luck-event--quests${showQuests ? " is-active" : ""}`}
              aria-current={showQuests ? "true" : undefined} disabled={busy}
              onClick={() => onTabChange?.("quests")}>
              {claimableQuests > 0 && <Badge bg="danger" className="luck-event-badge">{claimableQuests}</Badge>}
              <img src="/assets/images/icons/leaderboard.png" alt="" width={22} height={22} />
              <span className="luck-event-label"><span>{t("navigation:quests")}</span></span>
            </button>
          )}
          {events.map(event => {
            const selected = event.playerWheelOfLuckId === model.selectedId;
            // From the Quests tab, the selected wheel only switches back to it.
            const switchBack = showQuests && selected;
            return (
            <button type="button" key={event.playerWheelOfLuckId}
              className={`luck-event${selected && !showQuests ? " is-active" : ""}`}
              title={event.title}
              aria-current={selected && !showQuests ? "true" : undefined}
              disabled={!switchBack && (busy || loading || Boolean(pending) || Boolean(historicalKeyId))}
              onClick={() => {
                if (!switchBack) model.selectWheel(event.playerWheelOfLuckId);
                if (showQuests) onTabChange?.("wheel");
              }}>
              <img src="/assets/images/wheel/events/wheel-icon.png" alt="" width={22} height={22} />
              <span className="luck-event-label"><span>{event.title}</span>{event.allSlotsClaimed && <small>{t("wheel:waitForReset")}</small>}</span>
            </button>
            );
          })}
          {historicalKeyId && <div className="luck-historical-label">{t("wheel:leftoverKey")}</div>}
        </nav>
        {showQuests ? (
        <section className="luck-panel luck-quests" aria-label={t("navigation:quests")}>
          <QuestList onNavigate={close} />
        </section>
        ) : (
        <section className="luck-panel" data-theme={wheel?.theme || "Daily"} aria-label={t("wheel:selectedWheel")}>
          {loading && !wheel && <div className="luck-loading" role="status"><CircularProgress />{t("wheel:loading")}</div>}
          {model.error && <div className="luck-error" role="alert">{model.error}
            {!pending && !busy && <button type="button" onClick={() => model.reload()} disabled={loading}>{t("wheel:reload")}</button>}
          </div>}
          {!loading && !wheel && !model.error && <div className="luck-empty">{t("wheel:empty")}</div>}
          {/* Kept on screen while it reloads, so it neither blanks nor snaps back to its start angle. */}
          {wheel && <>
            <header className="luck-panel-header">
              <div className="luck-panel-tools">
                {wheel.nextResetAt && <span>{t("wheel:resetIn")} <strong>{timeLeft(wheel.nextResetAt)}</strong></span>}
                <button type="button" className="luck-info-button" aria-label={t("wheel:viewRewards")} aria-haspopup="dialog" aria-expanded={showInfo} onClick={() => setShowInfo(true)} disabled={busy}>
                  <img src="/assets/images/wheel/events/info.svg" alt="" width={18} height={18} />
                </button>
              </div>
            </header>
            {wheel.description?.trim() && <p className="luck-description" title={wheel.description}>{wheel.description}</p>}
            <LuckyWheel key={wheel.playerWheelOfLuckId} wheelImage={wheel.wheelImage} pointerImage={wheel.pointerImage}
              slots={wheel.slots} result={model.spinResult}
              describe={describe} onComplete={model.completeSpin} />
            <div className="luck-balances">
              <span>{t("wheel:keys")} <strong>{number(wheel.validKeyCount)}</strong></span>
              {wheel.clegAccepted && <span><img src="/assets/images/icons/cleg.png" alt="" width={20} height={20} /> {t("wheel:clegAmount", { amount: number(clegBalance) })}</span>}
            </div>
            <div className="luck-payment" aria-live="polite">
              {pending && !busy ? <WheelButton onClick={() => model.spin(pending.paymentMethod)}>{t("wheel:retrySpin")}</WheelButton> :
                waiting ? <p className="luck-wait">{t("wheel:allClaimed", { total: number(WHEEL_SLOT_COUNT) })} {wheel.nextResetAt
                  ? t("wheel:nextWheelIn", { time: timeLeft(wheel.nextResetAt) }) : t("wheel:waitingForReset")}</p> : <>
                  {(canUseKey || canUseCleg) && <WheelButton gift={spinPaymentMethod === 1 && wheel.validKeyCount > 0}
                    disabled={busy || loading || (spinPaymentMethod === 1 ? wheel.validKeyCount < 1 : clegBalance < wheel.clegCost)}
                    onClick={() => model.spin(spinPaymentMethod)}>{busy ? t("wheel:spin.spinning") : <><strong>{t("wheel:spin.label")}</strong> <small>{spinPaymentMethod === 1
                      ? (wheel.automaticKeyCount ? t("wheel:spin.free") : t("wheel:spin.oneKey"))
                      : t("wheel:spin.cleg", { amount: number(wheel.clegCost) })}</small></>}</WheelButton>}
                  {!canUseKey && !canUseCleg && <p className="luck-wait">{ended ? t("wheel:eventEnded") : t("wheel:spinsUnavailable")}</p>}
                </>}
            </div>
            {!waiting && !pending && spinPaymentMethod === 1 && canUseKey && wheel.validKeyCount < 1 && <p className="luck-hint">{t("wheel:needKey")}</p>}
            {!waiting && !pending && spinPaymentMethod === 2 && canUseCleg && clegBalance < wheel.clegCost && <p className="luck-hint">{t("wheel:notEnoughCleg")}</p>}
          </>}
        </section>
        )}
      </div>
    </Dialog>
    <WheelRewardsInfo open={open && showInfo} onClose={() => setShowInfo(false)} wheel={wheel} serverNow={serverNow} describe={describe} />
    <WheelWinDialog open={open} reward={model.lastReward} presentation={reward} onClose={model.dismissReward} />
    </>
  );
}
